import {
    AuthService,
    ConfigurationService,
    DataClientError,
    DataServerConditionNotification,
    EntityData,
    LocalConfigurationService,
    LoginDetails,
    LoginType
} from "@fedelta-pos/data-client";
import {
    LoyaltyCardData,
    LoyaltyCardType,
    MemberSummaryContactData,
    MemberSummaryData,
    MemberSummaryEntityModel
} from "./member-summary-entity.model";
import {computed, inject, Injectable, Signal, signal, untracked, WritableSignal} from "@angular/core";
import {Local} from "../configuration/config";
import {SessionModel} from "../session/session.model";
import {onEach} from '../utils/observable-utils';
import {effectOnSignalSet} from '../utils/signal-utils';
import {MessageDetails, MessageLevel} from "../notification/message/message";
import {NotificationService} from "../notification/notification.service";
import {DatePipe} from "@angular/common";
import {OrderingContextModel} from '../ordering-context/ordering-context.model';
import {IdentityData} from "./identity-entity.model";
import {DealData} from '../deal/deal-entity.model';
import {AddressData} from '../customer-info/address.data';
import {MenuModel} from '../menu/menu.model';
import {AppDeviceLoginActionData, AppDeviceLoginService} from '../app-device/app-device-login.service';
import {AppDeviceLogoutActionData, AppDeviceLogoutService} from '../app-device/app-device-logout.service';
import {AppDeviceService} from '../app-device/app-device.service';
import {firstValueFrom, Subject} from 'rxjs';
import {ConfigurationModel} from '../configuration/configuration.model';
import {DeferredInjectable} from '../deferred-injection.service';

export enum LoginStatus {
    SUCCESS,
    STORE_INVALID,
    IDENTITY_HAS_NO_PASSWORD,
    IDENTITY_VERIFICATION_REQUIRED,
    FAILED
}

// noinspection JSUnusedGlobalSymbols
export enum VerificationCodeDeliveryMethod {
    EMAIL = "email",
    MOBILE = "mobile",
    PREFER_EMAIL = "prefer_email",
    PREFER_MOBILE = "prefer_mobile"
}

export class LoginResult {
    constructor(public status: LoginStatus,
                public verificationId?: string,
                public verificationCodeDeliveryMethod?: VerificationCodeDeliveryMethod,
                public verificationCodeDeliveryDetails?: string,
                public error?: any) {
    }
}

@Injectable({providedIn: "root"})
export class IdentityModel implements DeferredInjectable {
    private authService: AuthService = inject(AuthService);
    private memberSummaryModel: MemberSummaryEntityModel = inject(MemberSummaryEntityModel);
    private configurationModel: ConfigurationModel = inject(ConfigurationModel);
    private sessionModel: SessionModel = inject(SessionModel);
    private appDeviceService: AppDeviceService = inject(AppDeviceService);
    private orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    private appDeviceLoginService: AppDeviceLoginService = inject(AppDeviceLoginService);
    private appDeviceLogoutService: AppDeviceLogoutService = inject(AppDeviceLogoutService);
    private notificationService: NotificationService = inject(NotificationService);
    private configurationService: ConfigurationService = inject(ConfigurationService);
    private localConfigurationService: LocalConfigurationService = inject(LocalConfigurationService);
    private menuModel: MenuModel;

    // Tracks the details of the current logged-in member
    public readonly _currentMember: WritableSignal<MemberSummaryData> = signal<MemberSummaryData>(undefined);
    public readonly currentMember: Signal<MemberSummaryData> = this._currentMember.asReadonly();

    // Tracks the current identity (customer details obtained from, and for updates to, the logged-in member)
    public readonly currentIdentity: WritableSignal<IdentityData> = signal<IdentityData>(EntityData.create(IdentityData));

    // Tracks the waiter name
    public readonly waiterName: WritableSignal<string> = signal<string>(undefined);

    private readonly _isGuestUser: WritableSignal<boolean> = signal<boolean>(true);
    public readonly isGuestUser: Signal<boolean> = this._isGuestUser.asReadonly();

    public readonly currentMembershipTypeId: Signal<number> = computed<number>(() =>
        this._currentMember() ? this._currentMember().membershipType.membershipTypeId : undefined);

    public readonly loginIdentity: Signal<string> = computed(() =>
        this.configurationModel.loginIdentityIsEmail() ? this.currentIdentity().email
            : this.configurationModel.loginIdentityIsMobile() ? this.currentIdentity().mobilePhone
                : this.configurationModel.loginIdentityIsMemberNo() ? this.currentIdentity().memberNo
                    : undefined);

    public readonly loyaltyCards: Signal<LoyaltyCardData[]> = computed<LoyaltyCardData[]>(() => {
        const cardCompletionFn: (card: LoyaltyCardData) => number = this.cardCompletion();

        return this._currentMember() ? this._currentMember().loyaltyCards.sort((a: LoyaltyCardData, b: LoyaltyCardData) => cardCompletionFn(b) - cardCompletionFn(a)) : [];
    });

    public readonly hasActiveLoyaltyCard: Signal<boolean> = computed<boolean>(() =>
        this._currentMember() && !!this._currentMember().loyaltyCards.length);

    public readonly tickets: Signal<DealData[]> = computed<DealData[]>(() =>
        this._currentMember().deals.filter((deal: DealData) => deal.isTicket && deal.isAvailableForOrderType(this.orderingContextModel.orderType())));

    /**
     * Notifies revocation of a login token
     */
    public readonly loginRevoked$: Subject<void> = new Subject();

    /**
     * Notifies successful login
     */
    public readonly loginComplete$: Subject<void> = new Subject();

    public ticketUsage: { [key: string]: number } = {};

    constructor() {
        console.log("--- IdentityModel");

        onEach(this.authService.loginDetails$, (loginDetails: LoginDetails) => {
            this.configurationService.setContext("userId", loginDetails.userId ? loginDetails.userId.toString() : undefined);
            this.configurationService.setContext("webTerminalId", loginDetails.webTerminalId ? loginDetails.webTerminalId.toString() : undefined);
            this.configurationService.setContext("outletId", loginDetails.outletId ? loginDetails.outletId.toString() : undefined);
            this._isGuestUser.set(this.authService.getLoggedInType() === LoginType.GUEST);
        });

        onEach(this.authService.loginRevoked$, () => this.loginRevoked());

        onEach(this.sessionModel.sessionReset$, () => {
            this._currentMember.set(undefined);
            const firstName = this.currentIdentity().firstName;
            this.currentIdentity.set(EntityData.create(IdentityData));
            this.currentIdentity().firstName = firstName;
            this.currentIdentity.update(i => <IdentityData>EntityData.create(IdentityData).assign(i));
        });

        // Side effects when waiterName changes
        effectOnSignalSet(this.waiterName, async (previousWaiterName: string) => {
            if (untracked(() => this.appDeviceService.isDevice())) {
                if (previousWaiterName) {
                    const logoutRequest: AppDeviceLogoutActionData = EntityData.create(AppDeviceLogoutActionData);
                    logoutRequest.waiterName = previousWaiterName;
                    logoutRequest.deviceName = untracked(() => this.appDeviceService.deviceName());
                    await this.appDeviceLogoutService.performAction(logoutRequest);
                }
                if (this.waiterName()) {
                    const loginRequest: AppDeviceLoginActionData = EntityData.create(AppDeviceLoginActionData);
                    loginRequest.waiterName = this.waiterName();
                    loginRequest.deviceName = untracked(() => this.appDeviceService.deviceName());
                    await this.appDeviceLoginService.performAction(loginRequest);
                }
            }
        });
    }

    public inject(): void {
        this.menuModel = inject(MenuModel);
    }

    public readonly promoteMemberLogin: Signal<boolean> = computed<boolean>(() =>
        this.configurationModel.app()?.promoteMemberLogin && this.configurationModel.allowMemberLogin() && this.configurationModel.isConsumerActor() && this._isGuestUser());

    public readonly cardCompletion: Signal<(card: LoyaltyCardData) => number> = computed(() => {
        const currentMember: MemberSummaryData = this._currentMember();

        return (card: LoyaltyCardData): number => {
            return card.cardType === LoyaltyCardType.STAMPS ? card.purchaseCount / card.goal : Math.min(1, currentMember.pointsBalance / card.pointsRequiredToRedeem);
        };
    });

    public async loadMemberSummary(): Promise<void> {
        try {
            await this.memberSummaryModel.loadEntity(undefined, {localDateTime: EntityData.formatLocalDateTime(this.configurationModel.now)});
        } catch (error) {
            console.error(error);
            throw error;
        }

        this.setCurrentMember(this.memberSummaryModel.dataSignal());
    }

    private async loginRevoked(): Promise<void> {
        console.log("Login service - Login revoked");
        this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.identification.revoked"));
        if ((await this.loginAsGuest()).status === LoginStatus.SUCCESS) {
            this.loginRevoked$.next();
        } else {
            void this.sessionModel.goToEntryPage();
        }
    }

    /**
     * Login without an identity
     */
    public async loginAsGuest(): Promise<LoginResult> {
        if (this.authService.isLoggedIn()) {
            if (this._isGuestUser()) {
                return new LoginResult(LoginStatus.SUCCESS);
            }
            this.authService.logout();
            this.setCurrentMember(undefined);
            this.menuModel.unloadMenu();
        }

        this.currentIdentity.set(EntityData.create(IdentityData));

        const user: string = this.configurationModel.previewToken() ? "fedelta-temp-app-token" : "fedelta-app-token";
        const password: string = await this.localConfigurationService.getString(Local.LOGIN_TOKEN);
        const appId: number = await this.localConfigurationService.getNumber(Local.APP_ID);

        try {
            if (!await firstValueFrom(this.authService.login(user, password, LoginType.GUEST, {
                appId: appId
            }))) {
                this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.identification.cannotStart"));
                return new LoginResult(LoginStatus.FAILED);
            }

            if (!await this.orderingContextModel.validateAndReapplyCurrentStore()) {
                void this.sessionModel.goToLandingPage();
                return new LoginResult(LoginStatus.STORE_INVALID);
            }

            this.loginComplete$.next();
            return new LoginResult(LoginStatus.SUCCESS);
        } catch (error) {
            console.log("Error during guest login attempt:", error);
            this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.common.cannotCommunicateServer"));
            return new LoginResult(LoginStatus.FAILED, undefined, undefined, undefined, error);
        }
    }

    public async loginAsMember(loginType: LoginType, remember: boolean, identity: string, identityPassword: string, verificationId?: string, verificationCode?: string): Promise<LoginResult> {
        const wasLoggedIn: boolean = this.authService.isLoggedIn();
        if (wasLoggedIn) {
            this.authService.logout();
            this.setCurrentMember(undefined);
            this.menuModel.unloadMenu();
        }

        const user: string = this.configurationModel.previewToken() ? "fedelta-temp-app-token" : "fedelta-app-token";
        const password: string = await this.localConfigurationService.getString(Local.LOGIN_TOKEN);
        const appId: number = await this.localConfigurationService.getNumber(Local.APP_ID);
        try {
            if (!await firstValueFrom(this.authService.login(user, password, loginType, {
                appId: appId,
                identity: identity,
                identityPassword: identityPassword,
                remember: remember,
                verificationId: verificationId,
                verificationCode: verificationCode
            }))) {
                this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.identification.loginFailed"));
                return new LoginResult(LoginStatus.FAILED);
            }

            if (!await this.orderingContextModel.validateAndReapplyCurrentStore()) {
                void this.sessionModel.goToEntryPage();
                return new LoginResult(LoginStatus.STORE_INVALID);
            }

            this.loginComplete$.next();
            return new LoginResult(LoginStatus.SUCCESS);
        } catch (error) {
            const conditionNotification: DataServerConditionNotification = error instanceof DataClientError ? await error.conditionNotification : undefined;

            if (error.status === 401 && conditionNotification) {
                if (conditionNotification.conditionName === "login.login2FARequired") {
                    return new LoginResult(
                        LoginStatus.IDENTITY_VERIFICATION_REQUIRED,
                        conditionNotification.conditionParams["verificationId"],
                        conditionNotification.conditionParams["verificationCodeDeliveryMethod"],
                        conditionNotification.conditionParams["verificationCodeDeliveryDetails"]);
                }
                if (conditionNotification.conditionName === "login.noIdentityPassword") {
                    return new LoginResult(LoginStatus.IDENTITY_HAS_NO_PASSWORD);
                }
            }

            console.log("Error during member login attempt:", error);
            if (error.status === 404) {
                this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.common.cannotCommunicateServer"));
            } else if (error.status === 0) {
                this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.common.loadFailed"));
            } else if (error.status !== 401) {
                this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.identification.loginFailed"));
            }
            return new LoginResult(LoginStatus.FAILED, undefined, undefined, undefined, error);
        } finally {
            if (wasLoggedIn && !this.authService.isLoggedIn()) {
                try {
                    await this.loginAsGuest();
                } catch (ignore) {
                }
            }
        }
    }

    /**
     * Login using member data that has been provided from an external source
     */
    public async loginAsExternalMember(memberData: string): Promise<void> {
        console.log("Logging in using: " + memberData);
        try {
            if (this.authService.isLoggedIn()) {
                this.authService.logout();
            }
            await firstValueFrom(this.authService.login("fedelta-app-token", await this.localConfigurationService.getString(Local.LOGIN_TOKEN), LoginType.MEMBER_DELEGATED, {
                appId: await this.localConfigurationService.getNumber(Local.APP_ID),
                identity: memberData,
                remember: true
            }));
        } catch (e) {
            console.error("Failed to load member");
            console.error(e);
        }
    }

    private setCurrentMember(member: MemberSummaryData) {
        const previousMemberContact: MemberSummaryContactData = this.getMemberContact(this._currentMember());
        const newMemberContact: MemberSummaryContactData = this.getMemberContact(member);
        if (newMemberContact) {
            // Copy the member details to the logged-in identity (rather than retrieving the contact separately, we load it with the member)
            this.currentIdentity().id = newMemberContact.id;
            this.currentIdentity().email = newMemberContact.email;
            this.currentIdentity().mobilePhone = newMemberContact.mobilePhone;
            this.currentIdentity().memberNo = member.integratedLoyaltyId || member.memberNo;
            this.currentIdentity().firstName = newMemberContact.firstName;
            this.currentIdentity().lastName = newMemberContact.lastName;
            this.currentIdentity().dob = newMemberContact.dob ? new DatePipe("en-US").transform(member.contact.dob, "yyyy-MM-dd") : undefined;
            this.currentIdentity().gender = newMemberContact.gender;
            this.currentIdentity().deliveryInstructions = newMemberContact.deliveryInstructions;
            this.currentIdentity().homeAddress = <AddressData>EntityData.create(AddressData).assign(newMemberContact.homeAddress);
            this.currentIdentity.update(i => <IdentityData>EntityData.create(IdentityData).assign(i));
        } else if (previousMemberContact) {
            // Logged out as member
            this.currentIdentity.set(EntityData.create(IdentityData));
        }

        this.orderingContextModel.applyIdentityContactDetails(this.currentIdentity());

        if (this._currentMember() !== member && (!this._currentMember() || !member || this._currentMember().toJSONString() !== member.toJSONString())) {
            this._currentMember.set(member);
        }
    }

    public applyIdentity(identity: IdentityData): void {
        this.currentIdentity().assign(identity);
        this.currentIdentity.update(i => <IdentityData>EntityData.create(IdentityData).assign(i));
        this.orderingContextModel.applyIdentityContactDetails(this.currentIdentity());

        const currentContact: MemberSummaryContactData = this.getMemberContact(this._currentMember());
        if (currentContact) {
            currentContact.id = this.currentIdentity().id;
            currentContact.email = this.currentIdentity().email;
            currentContact.mobilePhone = this.currentIdentity().mobilePhone;
            currentContact.firstName = this.currentIdentity().firstName;
            currentContact.lastName = this.currentIdentity().lastName;
            currentContact.dob = this.currentIdentity().dob ? new Date(this.currentIdentity().dob) : undefined;
            currentContact.gender = this.currentIdentity().gender;
            currentContact.deliveryInstructions = this.currentIdentity().deliveryInstructions;
            currentContact.homeAddress.assign(this.currentIdentity().homeAddress);
            this._currentMember.update(m => <MemberSummaryData>EntityData.create(MemberSummaryData).assign(m));
        }
    }

    private getMemberContact(member: MemberSummaryData): MemberSummaryContactData {
        return member?.memberId && member!.memberId !== "-1" && member!.contact && member!.contact.id ? member!.contact : undefined;
    }
}
